import {FC, useState, FormEvent, useRef, useEffect} from 'react'
import {useFormik} from 'formik'
import {useSearchParams} from 'react-router-dom'
import clsx from 'clsx'
import {useIntl} from 'react-intl'
import {useMutation} from 'react-query'
import {Toast, AlertBoostrapButtons} from '../../../../../../../../_metronic/partials/widgets'
import {initialTaskComment, ResponseTaskComment} from './core/_models'
import {Comment} from '../../../core/_models'
import {useThemeMode} from '../../../../../../../../_metronic/partials'
import {UserAvatar} from '../../../../../../../../_metronic/helpers'
import {KTSVG, ID} from '../../../../../../../../_metronic/helpers'
import {createTaskComment, updateTaskComment, deleteTaskComment, processInlineBase64Images} from './core/_requests'
import {useAuth} from '../../../../../../auth'
import {RichCommentEditor} from './components/RichCommentEditor'
import {CommentContent} from './components/CommentContent'
import './components/TaskComments.css'

const auditableColumns: Record<string, string> = {
  name: 'título da tarefa',
  begin_date: 'data inicial',
  begin_time: 'início',
  end_time: 'término',
  total_time: 'tempo trabalhado',
  play: 'play',
  status: 'status',
  user: 'usuário',
  expected_date: 'data prevista de entrega',
  project_id: 'projeto',
  description: 'descrição',
  priority: 'prioridade',
}

type TaskCommentsProps = {
  comments: Comment[]
}

export const TaskComments: FC<TaskCommentsProps> = ({comments}) => {
  const {mode} = useThemeMode()
  const [searchParams] = useSearchParams()
  const taskId = searchParams.get('taskId') as ID
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [localComments, setLocalComments] = useState<Comment[]>(comments)
  const {currentUser} = useAuth()
  const intl = useIntl()
  const [showHint, setShowHint] = useState<boolean>(false)
  const [commentAttachments, setCommentAttachments] = useState<string[]>([])
  const commentsListRef = useRef<HTMLDivElement>(null)

  const formik = useFormik({
    initialValues: initialTaskComment,
    onSubmit: async (values, {resetForm}) => {
      setIsDisabled(true)

      values.task_id = taskId

      // Upload inline base64 images and replace with storage URLs so content is not truncated in DB
      if (values.content && values.content.includes('data:image') && taskId) {
        try {
          const { html, removedCount } = await processInlineBase64Images(values.content, taskId)
          values.content = html
          if (removedCount > 0) {
            Toast.fire({
              icon: 'warning',
              title: removedCount === 1
                ? 'Uma imagem foi removida (máx. 3 MB ou falha no envio).'
                : `${removedCount} imagens foram removidas (máx. 3 MB ou falha no envio).`,
              customClass: {
                popup: 'bg-light-warning',
                title: 'text-warning',
              },
            })
          }
        } catch (e) {
          console.error('Error processing inline base64 images:', e)
        }
      }

      // Extract image paths from HTML content and add to attachments
      if (values.content) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = values.content
        const images = tempDiv.querySelectorAll('img')
        const imagePaths: string[] = []
        
        images.forEach((img) => {
          // First try to get path from data attribute (newly uploaded)
          let path = img.getAttribute('data-image-path')
          
          // If not found, try to extract from src URL
          if (!path && img.src) {
            const src = img.src
            // Extract path from URL like: http://domain/storage/tasks/filename.jpg
            const storageMatch = src.match(/storage\/(tasks\/[^"'\s]+)/)
            if (storageMatch) {
              path = storageMatch[1]
            }
          }
          
          if (path && !imagePaths.includes(path)) {
            imagePaths.push(path)
          }
        })
        
        // Merge with attachments from editor (remove duplicates)
        const allAttachments = Array.from(new Set([...commentAttachments, ...imagePaths]))
        if (allAttachments.length > 0) {
          values.attachments = allAttachments
        }
      }

      try {
        let response: ResponseTaskComment | undefined

        if (values.id) {
          response = await updateTaskComment(values)

          setLocalComments((prev) =>
            prev.map((comment) =>
              comment.id === values.id ? {...comment, content: values.content!} : comment
            )
          )
        } else {
          response = await createTaskComment(values)

          setLocalComments((prev) => [response?.comment!, ...prev])
          
          // Scroll to show new comment after state update
          setTimeout(() => {
            if (commentsListRef.current) {
              // Scroll to top (in column-reverse, newest is at top)
              commentsListRef.current.scrollTop = 0
            }
          }, 100)
        }

        Toast.fire({
          icon: 'success',
          title: response?.message,
          customClass: {
            popup: 'bg-light-success',
            title: 'text-success',
          },
        })
      } catch (error: any) {
        console.error(error)
        Toast.fire({
          icon: 'error',
          title: error.response?.data?.message || 'Erro ao salvar comentário',
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
      } finally {
        resetForm()
        setCommentAttachments([])
        // Force clear editor content to trigger preview cleanup
        formik.setFieldValue('content', '')
      }
    },
  })

  const handleEditComment = (commentId: ID, content: string) => {
    formik.setFieldValue('id', commentId)
    formik.setFieldValue('content', content)
    setIsDisabled(false)
  }

  const handleCancelEditComment = () => {
    formik.resetForm()
    setIsDisabled(true)
  }

  const confirmTaskCommentDelete = (id: ID) => {
    AlertBoostrapButtons.fire({
      title: intl.formatMessage({id: 'ALERT.LABEL.ARE_YOU_SURE'}),
      html: `<h5>${intl.formatMessage({id: 'ALERT.LABEL.IT_CAN_NOT_BE_UNDONE'})}<h5>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'GENERAL.YES_EXCLUDE'}),
      cancelButtonText: intl.formatMessage({id: 'GENERAL.CANECEL'}),
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteItem.mutate(id)
      }
    })
  }

  const deleteItem = useMutation((id: ID) => deleteTaskComment(id), {
    onSuccess: (response, id) => {
      removeCommentById(id)

      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_DELETE_ITEM'},
          {item: intl.formatMessage({id: 'TASK.COMMENT'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
    },
  })

  const removeCommentById = (id: ID) => {
    setLocalComments((prev) => prev.filter((comment) => comment.id !== id))
  }

  // Check if comment has rich content (HTML) or is plain text
  const hasRichContent = (comment: Comment): boolean => {
    if (!comment.content) return false
    // Check if content has HTML tags (excluding event comments)
    if (comment.event) return false
    // Check if it's HTML (has tags) or plain text
    const hasHtmlTags = /<[^>]+>/.test(comment.content)
    return hasHtmlTags || !!(comment.mentions && comment.mentions.length > 0) || !!(comment.attachments && comment.attachments.length > 0)
  }

  return (
    <div className='task-comments-container'>
        <div className='task-comments-list' ref={commentsListRef}>
          {localComments?.map((comment, index) => (
            <div
              key={comment.id || index}
              className={clsx(
                'd-flex w-100 py-2',
                !comment.event && 'justify-content-end',
                comment.id === formik.values.id ? 'bg-light-primary' : ''
              )}
            >
              <div
                className={clsx(
                  'w-100 d-flex align-items-center space-4 bg-gray-200 rounded px-2 py-2 position-relative',
                  !comment.event
                    ? 'bg-light-warning flex-row-reverse w-fit-content align-self-end ms-10'
                    : '',
                  comment.id === formik.values.id ? 'bg-light-primary' : ''
                )}
              >
                <div className={clsx(!comment.event ? 'ms-5' : 'me-5')}>
                  <UserAvatar
                    name={comment.user?.name ?? ''}
                    avatar={comment.user?.avatar}
                    sizeClass='symbol-30px'
                    imgClassName='object-fit-cover'
                  />
                </div>

                <div>
                  <div>
                    <small className='text-gray mb-1'>
                      {comment.user_name && <strong>{comment.user_name}</strong>}{' '}
                      <i>
                        {new Date(
                          comment.when ? comment.when.toString() : comment.created_at!
                        ).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </i>
                    </small>
                  </div>
                  <div 
                    className={clsx('mb-0', !comment.event && 'comment-text-plain')}
                  >
                    {comment.event ? (
                      <>
                        {comment.user?.name}{' '}
                        {comment.event == 'updated' && (
                          <>
                            {comment.auditable_type == 'App\\Models\\Api\\TimeTracking' ? (
                              `pausou o trabalho na tarefa.`
                            ) : comment.auditable_type == 'App\\Models\\Api\\TaskChecklist' ? (
                              comment.details &&
                              Object.keys(comment.details.new).map((key, index) =>
                                key == 'name'
                                  ? `renomeou o checklist ${comment?.details?.old[key]} para ${comment?.details?.new[key]}`
                                  : `${
                                      !!comment?.details?.new[key] ? 'marcou ' : 'desmarcou '
                                    } o checklist ${comment?.details?.old['name']}`
                              )
                            ) : (
                              <>
                                {comment.details &&
                                  Object.keys(comment.details.old)
                                    .map((key, index) => {
                                      // Pular campos que não devem aparecer nos logs
                                      if (['end_date', 'updated_at'].includes(key)) {
                                        return null
                                      }

                                      // Verificar se o campo tem um nome mapeado
                                      const fieldName = auditableColumns[key] || key

                                      // Se o campo não tem nome mapeado, pular
                                      if (!auditableColumns[key]) {
                                        return null
                                      }

                                      const oldValue = comment.details?.old[key]
                                      const newValue = comment.details?.new[key]

                                      return (
                                        <span key={`${comment.id}-${key}-${index}`}>
                                          {oldValue
                                            ? key !== 'project_id'
                                              ? key === 'description'
                                                ? `Atualizou o campo ${fieldName}`
                                                : `Atualizou o campo ${fieldName} de ${
                                                    key === 'expected_date' || key === 'begin_date'
                                                      ? oldValue
                                                        ? new Date(
                                                            oldValue.toString()
                                                          ).toLocaleDateString('pt-BR', {
                                                            timeZone: 'UTC',
                                                          })
                                                        : 'não definido'
                                                      : oldValue
                                                  } para ${
                                                    key === 'expected_date' || key === 'begin_date'
                                                      ? newValue
                                                        ? new Date(
                                                            newValue.toString()
                                                          ).toLocaleDateString('pt-BR', {
                                                            timeZone: 'UTC',
                                                          })
                                                        : 'não definido'
                                                      : newValue
                                                  }. `
                                              : 'Atualizou o projeto. '
                                            : key === 'description'
                                            ? `definiu o campo ${fieldName}.`
                                            : `definiu o campo ${fieldName} para ${
                                                ['expected_date', 'begin_date'].includes(key)
                                                  ? newValue
                                                    ? new Date(
                                                        newValue.toString()
                                                      ).toLocaleDateString('pt-BR', {
                                                        timeZone: 'UTC',
                                                      })
                                                    : 'não definido'
                                                  : `"${newValue}"`
                                              }. `}
                                        </span>
                                      )
                                    })
                                    .filter(Boolean)}
                              </>
                            )}
                          </>
                        )}
                        {comment.event == 'created' && (
                          <>
                            {comment.auditable_type == 'App\\Models\\Api\\TimeTracking' && (
                              <>
                                {!!comment.details?.play
                                  ? 'iniciou trabalho na tarefa.'
                                  : comment.details?.total_time! >= 0
                                  ? 'adicionou tempo manualmente na tarefa.'
                                  : 'removeu tempo manualmente na tarefa.'}
                              </>
                            )}
                            {comment.auditable_type == 'App\\Models\\Api\\TaskAttachment' &&
                              'adicionou um anexo na tarefa.'}
                            {comment.auditable_type == 'App\\Models\\Api\\TaskResponsible' &&
                              `adicionou o usuário ${comment.details?.user_name} na tarefa.`}
                            {comment.auditable_type == 'App\\Models\\Api\\TaskChecklist' &&
                              `adicionou um checklist na tarefa.`}
                          </>
                        )}
                        {comment.event == 'deleted' && (
                          <>
                            {comment.auditable_type == 'App\\Models\\Api\\TaskAttachment' &&
                              'removeu um anexo da tarefa.'}
                            {comment.auditable_type == 'App\\Models\\Api\\TaskResponsible' &&
                              `removeu o usuário ${comment.details?.user_name} da tarefa.`}
                            {comment.auditable_type == 'App\\Models\\Api\\TaskChecklist' &&
                              `removeu o checklist ${comment.details?.old['name']} da tarefa.`}
                          </>
                        )}
                        {comment.event == 'checklist_updated' && <>{`atualizou o checklist.`}</>}
                      </>
                    ) : hasRichContent(comment) ? (
                      <CommentContent
                        content={comment.content || ''}
                        mentions={comment.mentions}
                        attachments={comment.attachments}
                      />
                    ) : (
                      <>{comment.content}</>
                    )}
                  </div>
                </div>
                {comment.user_id == currentUser?.id && !comment.event && (
                  <div
                    className={clsx(
                      'd-flex justify-content-end flex-wrap position-absolute top-0',
                      !comment.event ? 'end-100 pe-2' : 'start-100 ps-2'
                    )}
                  >
                    <div
                      className='d-block btn btn-link btn-active-color-primary py-0 mb-2'
                      onClick={() =>
                        formik.values.id && formik.values.id === comment.id
                          ? handleCancelEditComment()
                          : handleEditComment(comment.id, comment.content!)
                      }
                    >
                      {formik.values.id && formik.values.id === comment.id ? (
                        <KTSVG
                          className='svg-icon-2 svg-icon-danger m-0'
                          path='/media/icons/duotune/arrows/arr061.svg'
                        />
                      ) : (
                        <KTSVG
                          path='/media/icons/duotune/art/art005.svg'
                          className='svg-icon-muted svg-icon-4 m-0'
                        />
                      )}
                    </div>
                    <div
                      className='d-block btn btn-link btn-active-color-primary py-0'
                      onClick={() => confirmTaskCommentDelete(comment.id)}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen027.svg'
                        className='svg-icon-muted svg-icon-4 m-0'
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className='task-comments-form'>
          <form className='d-flex flex-column p-1 pt-2' onSubmit={formik.handleSubmit}>
            <div className='d-flex align-items-end'>
              <div className='flex-grow-1'>
                <RichCommentEditor
                  value={formik.values.content || ''}
                  onChange={(value: string) => {
                    formik.setFieldValue('content', value)
                    setIsDisabled(!value.trim())
                  }}
                  onAttachmentsChange={(attachments: string[]) => {
                    setCommentAttachments(attachments)
                  }}
                  onSubmit={formik.handleSubmit}
                  placeholder='Adicione um comentário'
                  taskId={taskId}
                  disabled={formik.isSubmitting}
                />
              </div>
              <button
                disabled={isDisabled || formik.isSubmitting}
                className='btn btn-icon btn-icon-primary btn-light-primary ms-2 comment-submit-btn'
                type='submit'
              >
                <KTSVG path='/media/icons/duotune/general/gen016.svg' className='svg-icon-1' />
              </button>
            </div>
            <div
              className={clsx('text-muted fs-8 mt-1 comment-hint', showHint && 'comment-hint-visible')}
            >
              <i className='bi bi-info-circle me-1'></i>
              <span>Enter para enviar • Shift+Enter para quebra de linha • @ para mencionar • Ctrl+B/I para formatação</span>
            </div>
          </form>
        </div>
    </div>
  )
}
